import { createContext, useContext, useEffect, useState, createElement, type ReactNode } from 'react';
import type { Identity } from '../shared/types';
import { api, ApiRequestError } from './api';

const IdentityContext = createContext<Identity | null>(null);

/**
 * Loads the verified identity from the Worker.
 *
 * Each failure means something different and gets said differently. The first
 * version reported every failure as "you're not a household member" — which was a
 * confident, wrong diagnosis when the real problem was that the API wasn't
 * reachable on that hostname at all. Never state a cause you haven't established.
 */
function explain(err: unknown): string {
  if (err instanceof ApiRequestError) {
    if (err.body.error === 'api_unreachable') {
      return 'The app loaded, but the API is not answering on this address. Open the app at mangumhome.xyz — the API only runs there.';
    }
    if (err.status === 403) {
      return "You're signed in, but you're not set up as a household member yet. An adult needs to add you in Settings → Family, using this same email.";
    }
    if (err.status === 401) {
      return 'You are not signed in. Reload the page to sign in through Cloudflare Access.';
    }
    if (err.status >= 500) {
      return 'The API is up but returned an error. Try again in a moment.';
    }
  }
  return 'Could not reach the app. Check your connection and reload.';
}

export function IdentityProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<Identity>('/me')
      .then(setIdentity)
      .catch((err) => setError(explain(err)));
  }, []);

  if (error) {
    return createElement(
      'div',
      { className: 'grid min-h-screen place-items-center p-8 text-center text-ink-soft' },
      createElement('p', { className: 'max-w-sm leading-relaxed' }, error)
    );
  }
  if (!identity) {
    return createElement('div', {
      className: 'grid min-h-screen place-items-center text-ink-faint',
      children: 'Loading…',
    });
  }

  return createElement(IdentityContext.Provider, { value: identity }, children);
}

export function useIdentity(): Identity {
  const identity = useContext(IdentityContext);
  if (!identity) throw new Error('useIdentity must be used inside IdentityProvider');
  return identity;
}

export function useIsAdult(): boolean {
  return useIdentity().role === 'adult';
}
